<template>
  <div class="exp">
    <div class="exp__head">
      <h4 class="mb-0">Расходы</h4>
      <div class="exp__panel">
        <label class="exp__label mb-0" for="exp-object">Объект</label>
        <multiselect
          id="exp-object"
          class="exp__object"
          :value="selectedLocation"
          :options="filteredLocations"
          track-by="id"
          :custom-label="objectLabel"
          :internal-search="false"
          :show-labels="false"
          :allow-empty="false"
          :disabled="!locationsReady"
          placeholder="Выберите объект"
          @input="selectObject"
          @search-change="search = $event"
        >
          <template #option="{ option }">
            <div class="exp__option-name">{{ option.name }}</div>
            <div v-if="option.address" class="exp__option-address">{{ option.address }}</div>
          </template>
          <template #noResult>Ничего не найдено</template>
          <template #noOptions>Список объектов пуст</template>
        </multiselect>
        <span class="exp__label">с</span>
        <date-picker
          v-model="period.from"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
          :clearable="false"
          :editable="false"
          :disabled-date="disableAfterTo"
          input-class="form-control form-control-sm exp__from"
          placeholder="дд.мм.гггг"
        />
        <span class="exp__label">по</span>
        <date-picker
          v-model="period.to"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
          :clearable="false"
          :editable="false"
          :disabled-date="disableBeforeFrom"
          input-class="form-control form-control-sm exp__to"
          placeholder="дд.мм.гггг"
        />
        <button type="button" class="btn btn-success btn-sm exp__calc" :disabled="!canCalculate" @click="calculate">
          <span v-if="calculating" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Рассчитать
        </button>
      </div>
    </div>

    <div v-if="locations.status === 'error'" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center exp__banner exp__banner--locations" role="alert">
      <span>Не удалось загрузить объекты: {{ errorMessage(locations.error) }}</span>
      <button type="button" class="btn btn-sm btn-light ml-auto exp__retry" @click="retryLocations">Повторить</button>
    </div>
    <div v-if="result.status === 'error'" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center exp__banner exp__banner--result" role="alert">
      <span>Не удалось рассчитать расходы: {{ errorMessage(result.error) }}</span>
      <button type="button" class="btn btn-sm btn-light ml-auto exp__retry" :disabled="calculating" @click="retryCalculate">Повторить</button>
    </div>
    <div v-if="paramsChanged" class="exp__hint">Параметры изменены — нажмите «Рассчитать»</div>

    <div v-if="!booted" class="exp__loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
    </div>

    <template v-else>
      <div v-if="hasResult" class="exp__summary">
        <div class="exp__tile exp__tile--object">
          <div class="exp__tile-label">Объект</div>
          <div class="exp__tile-value exp__object-name" :title="objectInfo.name">{{ objectInfo.name || '—' }}</div>
          <div v-if="objectInfo.address" class="exp__tile-sub exp__object-address" :title="objectInfo.address">{{ objectInfo.address }}</div>
          <div v-if="objectInfo.code" class="exp__tile-sub exp__object-code" :title="objectInfo.code">{{ objectInfo.code }}</div>
        </div>
        <div class="exp__tile">
          <div class="exp__tile-label">Сумма по задачам</div>
          <div class="exp__tile-value exp__works-total">{{ formatRub(summary.worksTotal) }}</div>
        </div>
        <div class="exp__tile">
          <div class="exp__tile-label">Расходы, добавленные вручную</div>
          <div class="exp__tile-value exp__other-total">{{ formatRub(summary.otherTotal) }}</div>
        </div>
        <div class="exp__tile exp__tile--total">
          <div class="exp__tile-label">Общая сумма</div>
          <div class="exp__tile-value exp__total">{{ formatRub(summary.total) }}</div>
        </div>
      </div>

      <div class="exp__list-head">
        <h5 class="mb-0 exp__list-title">Расходы за период <span v-if="hasResult" class="exp__count">({{ rows.length }})</span></h5>
        <span v-if="calculating" class="spinner-border spinner-border-sm exp__list-spinner" role="status"><span class="sr-only">Расчёт…</span></span>
        <button type="button" class="btn btn-success btn-sm exp__add" :disabled="!canAdd" @click="openCreate">Добавить расход</button>
      </div>

      <div class="exp__body">
        <ExpensesTable v-if="hasResult" :rows="rows" />
        <div v-else-if="result.status === 'idle' && !calculating" class="exp__empty">Выберите объект и период, затем нажмите «Рассчитать»</div>
      </div>
    </template>

    <ExpenseFormModal
      v-if="expenseModal"
      :key="'exp-' + expenseModal.location.id"
      :location="expenseModal.location"
      @saved="onExpenseSaved"
      @closed="expenseModal = null"
    />
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import DatePicker from 'vue2-datepicker';
import ExpensesTable from '@/components/management/dispatcher/expenses/ExpensesTable.vue';
import ExpenseFormModal from '@/components/management/dispatcher/expenses/ExpenseFormModal.vue';
import * as api from '@/components/management/dispatcher/api';
import {
  createResource, createValueResource, loadResource, isReady, errorMessage,
} from '@/components/management/dispatcher/resource';
import { todayIso, formatRub, isoToRu } from '@/components/management/dispatcher/format';
import { isValidPeriod } from '@/components/management/dispatcher/period';
import { notifyError, notifyInfo } from '@/components/management/dispatcher/notify';
import {
  readPeriod, readObjectId, expensesQuery, sameParams, inPeriod, filterObjects, objectLabel, objectInfo, buildRows, summaryModel,
} from '@/components/management/dispatcher/expensesLogic';

/**
 * «Расходы» section owner (KOP-39 spec §6): objects list, selection + period, the report of one
 * object for one period (calculated only on demand), and the manual-expense modal.
 */
export default {
  name: 'ExpensesSection',
  components: { Multiselect, DatePicker, ExpensesTable, ExpenseFormModal },
  props: {
    layoutVersion: { type: Number, default: 0 },
  },
  data() {
    return {
      booted: false,
      locations: createResource(),
      result: createValueResource(),
      selectedId: null,
      period: readPeriod(this.$route && this.$route.query, todayIso()),
      calculated: null, // { objectId, period } of the latest request (and of the URL)
      calculating: false, // a report request is in flight; the shown report stays until it settles
      shownObjectId: null, // object of the report in `result` (lags `calculated` while a request runs)
      search: '',
      expenseModal: null,
    };
  },
  computed: {
    locationsReady() { return isReady(this.locations); },
    selectedLocation() { return this.locations.data.find((l) => l.id === this.selectedId) || null; },
    filteredLocations() { return filterObjects(this.locations.data, this.search); },
    hasResult() { return isReady(this.result) && !!this.result.data; },
    canCalculate() { return !!this.selectedId && isValidPeriod(this.period) && !this.calculating; },
    canAdd() { return !!this.selectedLocation && !this.expenseModal; },
    currentParams() { return { objectId: this.selectedId, period: { from: this.period.from, to: this.period.to } }; },
    paramsChanged() { return !!this.calculated && !sameParams(this.currentParams, this.calculated); },
    rows() { return buildRows(this.result.data); },
    summary() { return summaryModel(this.result.data); },
    objectInfo() {
      const id = this.shownObjectId;
      return objectInfo(this.locations.data.find((l) => l.id === id) || null, this.result.data);
    },
  },
  watch: {
    '$route.query': function onQuery(query) { this.applyQuery(query); },
  },
  created() {
    this.inFlight = {}; // non-reactive: de-duplicates concurrent reloads per key
    this.queued = {}; // non-reactive: one coalesced trailing reload per key for `fresh` callers
    this.seq = 0; // non-reactive: id of the latest report request
    this.loadInitial();
  },
  methods: {
    errorMessage,
    formatRub,
    objectLabel,
    /* ---------- loading ---------- */
    async loadInitial() {
      await this.reloadLocations();
      this.applyQuery(this.$route && this.$route.query);
      this.booted = true;
    },
    /**
     * Reuses an in-flight load. With `fresh`, a load already in flight is followed by one more
     * run (coalesced across callers) so a post-mutation read never settles on a pre-mutation response.
     */
    dedupe(key, run, fresh = false) {
      const current = this.inFlight[key];
      if (current) {
        if (!fresh) return current;
        if (!this.queued[key]) {
          this.queued[key] = current.then(() => { this.queued[key] = null; return this.dedupe(key, run); });
        }
        return this.queued[key];
      }
      const promise = run().finally(() => { this.inFlight[key] = null; });
      this.inFlight[key] = promise;
      return promise;
    },
    reloadLocations(fresh = false) {
      return this.dedupe('locations', () => loadResource(this.locations, api.getLocations), fresh);
    },
    async retryLocations() {
      await this.reloadLocations(true);
      if (isReady(this.locations) && !this.calculated) this.applyQuery(this.$route && this.$route.query);
    },
    /* ---------- selection, period, URL ---------- */
    knownId(id) {
      return !!id && this.locations.data.some((l) => l.id === id);
    },
    /** Applies ?object/from/to (boot and history): a known object calculates, an unknown one is dropped with a toast. */
    applyQuery(query) {
      const objectId = readObjectId(query);
      const period = readPeriod(query, todayIso());
      if (objectId && !this.knownId(objectId)) {
        if (!isReady(this.locations)) return; // retried from the banner later
        notifyError(this, 'Объект не найден');
        this.period = period;
        this.dropObjectFromQuery();
        return;
      }
      this.period = period;
      if (!objectId) return;
      this.selectedId = objectId;
      const params = { objectId, period };
      if (sameParams(params, this.calculated)) return; // our own replace() after a calculation
      this.runCalculation(params);
    },
    dropObjectFromQuery() {
      const current = { ...((this.$route && this.$route.query) || {}) };
      delete current.object;
      this.$router.replace({ query: current }).catch(() => {});
    },
    syncQuery(params) {
      const current = (this.$route && this.$route.query) || {};
      const next = expensesQuery(params.objectId, params.period);
      if (current.object === next.object && current.from === next.from && current.to === next.to) return;
      // vue-router 3.5 rejects replace() on a duplicated navigation; nothing to report.
      this.$router.replace({ query: { ...current, ...next } }).catch(() => {});
    },
    selectObject(location) {
      this.selectedId = location ? location.id : null;
    },
    disableAfterTo(date) { return todayIso(date) > this.period.to; },
    disableBeforeFrom(date) { return todayIso(date) < this.period.from; },
    /* ---------- calculation ---------- */
    calculate() {
      if (!this.canCalculate) return Promise.resolve();
      return this.runCalculation(this.currentParams);
    },
    retryCalculate() {
      return this.calculated ? this.runCalculation(this.calculated) : Promise.resolve();
    },
    /**
     * Latest request wins: an older response never overwrites a newer result. The shown report
     * stays visible while a request runs (`calculating`); a success replaces it, a failure clears it.
     */
    async runCalculation(target) {
      // Snapshot: applyQuery() hands over the very object it assigns to `period`, which the
      // date pickers then mutate in place — `calculated` must not follow those edits.
      const params = { objectId: target.objectId, period: { from: target.period.from, to: target.period.to } };
      this.calculated = params;
      this.syncQuery(params);
      this.seq += 1;
      const mine = this.seq;
      this.calculating = true;
      const fresh = createValueResource();
      const ok = await loadResource(fresh, () => api.getLocationExpenses({
        geo_location_id: params.objectId, date_from: params.period.from, date_to: params.period.to,
      }), { value: true });
      if (mine !== this.seq) return; // a newer request owns `result` and `calculating`
      this.calculating = false;
      this.result = fresh;
      this.shownObjectId = ok ? params.objectId : null;
      if (!ok) notifyError(this, fresh.error, 'Не удалось рассчитать расходы');
    },
    /* ---------- manual expense (spec §6.3) ---------- */
    openCreate() {
      if (!this.canAdd) return;
      this.expenseModal = { location: this.selectedLocation };
    },
    onExpenseSaved(saved) {
      this.expenseModal = null;
      const shown = this.calculated;
      if (!shown || Number(saved && saved.geo_location_id) !== shown.objectId) return Promise.resolve();
      const date = saved && saved.expense_date;
      if (date && !inPeriod(date, shown.period)) notifyInfo(this, `Расход от ${isoToRu(date)} вне выбранного периода`);
      return this.runCalculation(shown);
    },
  },
};
</script>

<style scoped lang="scss">
.exp { display: flex; flex-direction: column; height: 100%; min-height: 0; background: #fff; border: 1px solid #e3e5e1; border-radius: 8px; padding: 12px 16px; }
.exp__head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #eef0ec; margin-bottom: 10px; }
.exp__panel { margin-left: auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.exp__label { color: #4c524d; font-size: 13px; }
.exp__object { width: 360px; min-height: 29px; font-size: 13px; }
/*
 * Match the -sm pickers and button (28.9px measured). The global _select2.scss forces the tags to a
 * regular input height with !important, so the overrides need it too; the root is the component
 * element itself (vue-multiselect's `.multiselect { min-height: 40px }`), sized in `.exp__object` above.
 */
.exp__object ::v-deep .multiselect__tags { min-height: 29px !important; padding-top: 3px !important; padding-bottom: 0 !important; }
.exp__object ::v-deep .multiselect__select { height: 27px; }
.exp__object ::v-deep .multiselect__placeholder,
.exp__object ::v-deep .multiselect__single,
.exp__object ::v-deep .multiselect__input { margin-bottom: 3px !important; padding-top: 0 !important; font-size: 13px; line-height: 20px; min-height: 20px; }
.exp__option-name { font-weight: 600; }
.exp__option-address { color: #8b918c; font-size: 12px; }
.exp__panel ::v-deep .mx-datepicker { width: 140px !important; } /* the global _datepicker.scss forces 100% !important */
.exp__hint { color: #8b918c; font-size: 12.5px; margin-bottom: 8px; }
.exp__loading { flex: 1; display: flex; align-items: center; justify-content: center; }
.exp__summary { display: flex; gap: 12px; margin-bottom: 12px; }
.exp__tile { flex: 1 1 0; background: #f4f5f1; border: 1px solid #e3e5e1; border-radius: 10px; padding: 10px 12px; min-width: 0; }
.exp__tile--object { flex: 1.6 1 0; }
.exp__tile--total { border-color: #17876f; }
.exp__tile-label { color: #8b918c; font-size: 12px; margin-bottom: 4px; }
.exp__tile-value { font-weight: 700; font-size: 16px; color: #1d221f; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.exp__tile--total .exp__tile-value { color: #17876f; }
.exp__tile-sub { color: #4c524d; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.exp__list-head { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.exp__list-title { font-size: 15px; }
.exp__count { color: #8b918c; font-weight: 400; }
.exp__list-spinner { color: #8b918c; }
.exp__add { margin-left: auto; }
.exp__body { flex: 1; min-height: 0; overflow: auto; }
.exp__empty { color: #8b918c; font-size: 13px; padding: 18px 0; text-align: center; }
</style>
